import {
  CardTypeEnum,
  OrderPhysicalCardResultTypenames,
  ReplaceLostStolenCardResultTypenames,
} from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useFetchCardDetails } from 'features/cardManagement/hooks/useFetchCardDetails';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

type ResultTypenames =
  | keyof typeof OrderPhysicalCardResultTypenames
  | keyof typeof ReplaceLostStolenCardResultTypenames;

type Params = {
  title: string;
  message: string;
  resultType: ResultTypenames;
  cardId?: string;
  cardType?: string;
};
const NewCardOnTheWayScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'newCardOnTheWay',
  });

  const { sourceData } = useFetchCardDetails();

  const { title, message, resultType, cardId, cardType } =
    useLocalSearchParams<Params>();

  const replacedCardId = sourceData?.cards?.filter(
    (ele) => ele.type === cardType,
  )?.[0]?.id;

  const handleExitOrderPhysicalFlowAnalytics = usePressableAnalytics({
    analyticsId: 'orderPhysicalCardExitClicked',
    customAttributes: {
      card_type:
        cardType === CardTypeEnum.DebitCard ? 'debit_card' : 'charge_card',
    },
  });

  const isSuccess =
    resultType ===
      OrderPhysicalCardResultTypenames.OrderPhysicalCardResultSuccess ||
    resultType ===
      ReplaceLostStolenCardResultTypenames.ReplaceLostStolenCardSuccess;

  const { data, error, fetching, refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.QUICK_ACTION_TILES,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BANKING,
  ]);

  useEffect(() => {
    if (isSuccess && !fetching && (data || error)) {
      handleExitOrderPhysicalFlowAnalytics();
      router.replace({
        pathname: `/myvaro/card/[cardId]`,
        params: { cardId: replacedCardId ?? '' },
      });
    }
  }, [
    fetching,
    data,
    error,
    handleExitOrderPhysicalFlowAnalytics,
    isSuccess,
    replacedCardId,
    router,
  ]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      {isSuccess ? (
        <SuccessScreen
          visual="CARD_A_ANIMATED"
          titleText={title}
          content={
            <BodySmText style={styles.text} numberOfLines={0}>
              {message}
            </BodySmText>
          }
          primaryButton={{
            label: translatedText('done'),
            isLoading: fetching,
            onPress: refreshTiles,
          }}
        />
      ) : (
        <View style={styles.errorContainer}>
          <ErrorScreen
            screenType={ErrorScreenType.Warning}
            messageHeadline={title}
            messageBody={message}
            primaryButton={{
              title: translatedText('done'),
              onPress: () => {
                handleExitOrderPhysicalFlowAnalytics();
                router.replace(`/myvaro/card/${cardId ?? ''}`);
              },
            }}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    padding: theme.spacing.m,
  },
  text: {
    textAlign: 'center',
  },
});

export default NewCardOnTheWayScreen;
